import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, Landmark, Users, PlayCircle, CheckCircle2, AlertOctagon, Bot } from "lucide-react";

// Get Started — the 4-step launch checklist, driven entirely by
// GET /api/onboarding/checklist so a step can never claim to be done.
const META = {
  1: { icon: Building2, link: "/app/company", cta: "Open Company Settings →" },
  2: { icon: Landmark, link: "/app/company#funding", cta: "Set funding source →" },
  3: { icon: Users, link: "/app/employees", cta: "Manage roster →" },
  4: { icon: PlayCircle, link: "/app/payroll/new", cta: "Launch wizard →" },
};

export default function GetStarted() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/onboarding/checklist")).data); }
    catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Proactive trigger: a BLOCKED step opens Buddy once per tab with the
  // exact Execution Instruction needed to clear it.
  useEffect(() => {
    if (!data?.blocked_step) return;
    const key = `buddy-proactive-step-${data.blocked_step}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    window.dispatchEvent(new CustomEvent("buddy:proactive", {
      detail: {
        step: data.blocked_step,
        title: data.blocked_title,
        instruction: data.blocked_instruction,
      },
    }));
  }, [data]);

  if (!data || data.complete) return null;

  return (
    <div className="hard-card p-5 mb-6" data-testid="get-started-roadmap">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="overline">§ GET STARTED · LAUNCH CHECKLIST</div>
          <div className="font-heading font-black text-2xl">Go live in 4 steps</div>
        </div>
        <div className="font-mono text-xs text-muted-foreground" data-testid="onboarding-progress">
          {data.done_count} / {data.steps.length} complete
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {data.steps.map((s) => {
          const meta = META[s.step];
          const Icon = meta.icon;
          const blocked = s.status === "blocked";
          const done = s.status === "ok";
          return (
            <div key={s.step} data-testid={`onboarding-step-${s.step}`}
              className={"border-2 border-ink p-3 flex gap-3 " + (done ? "bg-good/10" : blocked ? "bg-bad/10" : "bg-white")}>
              <div className={"p-2 border-2 border-ink h-fit " + (done ? "bg-good text-white" : blocked ? "bg-bad text-white" : "bg-ink text-white")}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : blocked ? <AlertOctagon className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <div className="font-heading font-black text-sm">{s.step} · {s.title}</div>
                <p className="text-xs text-muted-foreground mt-1 leading-snug" data-testid={`onboarding-detail-${s.step}`}>
                  {s.detail}
                </p>
                {!done && (
                  <>
                    <p className="text-xs mt-2 leading-snug" data-testid={`onboarding-instruction-${s.step}`}>
                      {s.instruction}
                    </p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <Link to={meta.link} className="text-[10px] font-mono uppercase tracking-widest underline">{meta.cta}</Link>
                      <button
                        data-testid={`onboarding-ask-buddy-${s.step}`}
                        onClick={() => window.dispatchEvent(new CustomEvent("buddy:proactive", {
                          detail: { step: s.step, title: s.title, instruction: s.instruction },
                        }))}
                        className="text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 border-2 border-ink px-2 py-1 hover:bg-ink hover:text-white transition-colors"
                      >
                        <Bot className="w-3 h-3" /> Ask Buddy
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {data.blocked_step && (
        <p className="font-mono text-xs mt-4" data-testid="onboarding-blocked-banner">
          Step {data.blocked_step} · {data.blocked_title} is blocking payroll. Buddy has the fix.
        </p>
      )}
    </div>
  );
}
